import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useEffect, useMemo, useState } from "react";

import type { HelpArticleSummary } from "./types";

type HelpLibraryViewProps = {
  articles: HelpArticleSummary[];
  loading: boolean;
  error?: string;
  canPublish: boolean;
  customerId?: string | null;
  onOpenArticle: (slug: string) => void;
  onPublish?: () => void;
  onRetry?: () => void;
};

const formatUpdatedAt = (updatedAt: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(updatedAt));

export const HelpLibraryView = ({
  articles,
  loading,
  error,
  canPublish,
  customerId,
  onOpenArticle,
  onPublish,
  onRetry,
}: HelpLibraryViewProps) => {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  useEffect(() => {
    setSearch("");
    setSelectedCategory("All");
  }, [customerId]);
  const categories = useMemo(
    () => [
      "All",
      ...Array.from(new Set(articles.map(({ category }) => category))).sort(),
    ],
    [articles],
  );
  const visibleArticles = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return articles.filter((article) => {
      const categoryMatches =
        selectedCategory === "All" || article.category === selectedCategory;
      const searchMatches =
        !needle ||
        [article.title, article.summary, article.category].some((value) =>
          value.toLocaleLowerCase().includes(needle),
        );
      return categoryMatches && searchMatches;
    });
  }, [articles, search, selectedCategory]);

  return (
    <Box
      data-testid="help-library"
      sx={{
        flex: 1,
        overflowY: "auto",
        bgcolor: "atomic.surfaceTint",
        px: { xs: 2, sm: 3, lg: 5 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            px: { xs: 2.5, sm: 4.5 },
            py: { xs: 3, sm: 4 },
            borderRadius: { xs: 3, sm: 4 },
            color: "white",
            background:
              "linear-gradient(125deg, #173F91 0%, #2A5FDB 52%, #5E8DFA 100%)",
            boxShadow: "0 16px 44px rgba(42, 95, 219, 0.18)",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              width: 260,
              height: 260,
              borderRadius: "50%",
              right: -80,
              top: -125,
              bgcolor: "rgba(255,255,255,0.11)",
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            gap={2}
            sx={{ position: "relative" }}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                <AutoStoriesRoundedIcon />
                <Typography
                  component="h1"
                  sx={{ fontSize: { xs: 28, sm: 34 }, fontWeight: 700 }}
                >
                  Help center
                </Typography>
              </Stack>
              <Typography
                sx={{ maxWidth: 610, color: "rgba(255,255,255,.82)" }}
              >
                Practical guides for getting the most from Atomic, written for
                your team.
              </Typography>
            </Box>
            {canPublish && onPublish && (
              <Button
                variant="contained"
                startIcon={<EditNoteRoundedIcon />}
                onClick={onPublish}
                sx={{
                  flexShrink: 0,
                  bgcolor: "white",
                  color: "atomic.brandBlueDark",
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#F3F7FF" },
                }}
              >
                Publish article
              </Button>
            )}
          </Stack>
        </Box>

        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search articles, workflows, and topics"
            inputProps={{ "aria-label": "Search Help articles" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: "white",
              borderRadius: 2.5,
              "& .MuiOutlinedInput-root": { borderRadius: 2.5 },
            }}
          />
          {!loading && !error && articles.length > 0 && (
            <Stack
              direction="row"
              gap={1}
              mt={2}
              pb={0.5}
              sx={{ overflowX: "auto" }}
              aria-label="Article categories"
            >
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  clickable
                  color={selectedCategory === category ? "primary" : "default"}
                  variant={
                    selectedCategory === category ? "filled" : "outlined"
                  }
                  onClick={() => setSelectedCategory(category)}
                  sx={{ flexShrink: 0, fontWeight: 600 }}
                />
              ))}
            </Stack>
          )}
        </Box>

        {error ? (
          <Alert
            severity="error"
            sx={{ mt: 3, borderRadius: 2 }}
            action={
              onRetry ? (
                <Button color="inherit" size="small" onClick={onRetry}>
                  Try again
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        ) : loading ? (
          <Box
            aria-label="Loading Help articles"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              mt: 3,
            }}
          >
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} variant="rounded" height={196} />
            ))}
          </Box>
        ) : articles.length === 0 ? (
          <EmptyLibrary canPublish={canPublish} onPublish={onPublish} />
        ) : visibleArticles.length === 0 ? (
          <Box sx={{ py: 9, textAlign: "center" }}>
            <SearchRoundedIcon sx={{ fontSize: 44, color: "atomic.faint" }} />
            <Typography variant="h6" fontWeight={700} mt={1}>
              No articles match your search
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Try a different phrase or choose another category.
            </Typography>
            <Button
              sx={{ mt: 2 }}
              onClick={() => {
                setSearch("");
                setSelectedCategory("All");
              }}
            >
              Clear filters
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              mt: 3,
              pb: 4,
            }}
          >
            {visibleArticles.map((article) => (
              <Card
                key={article.slug}
                variant="outlined"
                sx={{
                  borderColor: "atomic.border",
                  borderRadius: 3,
                  bgcolor: "white",
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 28px rgba(16,17,19,.08)",
                  },
                }}
              >
                <CardActionArea
                  aria-label={`Open ${article.title}`}
                  onClick={() => onOpenArticle(article.slug)}
                  sx={{
                    height: "100%",
                    p: 2.5,
                    display: "flex",
                    alignItems: "stretch",
                  }}
                >
                  <Stack alignItems="flex-start" width="100%" minHeight={150}>
                    <Chip
                      label={article.category}
                      size="small"
                      sx={{
                        bgcolor: "atomic.blue1",
                        color: "atomic.brandBlueDark",
                        fontWeight: 700,
                      }}
                    />
                    <Typography variant="h6" fontWeight={700} mt={1.5}>
                      {article.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mt={0.75}
                      sx={{ lineHeight: 1.55 }}
                    >
                      {article.summary}
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      mt="auto"
                      pt={2}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Updated {formatUpdatedAt(article.updatedAt)}
                      </Typography>
                      <ArrowForwardRoundedIcon
                        fontSize="small"
                        sx={{ color: "atomic.brandBlue" }}
                      />
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const EmptyLibrary = ({
  canPublish,
  onPublish,
}: {
  canPublish: boolean;
  onPublish?: () => void;
}) => (
  <Box sx={{ py: { xs: 6, sm: 10 }, textAlign: "center" }}>
    <Box
      sx={{
        display: "inline-flex",
        p: 2,
        borderRadius: "50%",
        bgcolor: "atomic.blue1",
        color: "atomic.brandBlueDark",
      }}
    >
      <AutoStoriesRoundedIcon sx={{ fontSize: 34 }} />
    </Box>
    <Typography variant="h6" fontWeight={700} mt={2}>
      Your Help center is ready for its first article
    </Typography>
    <Typography color="text.secondary" mt={0.75}>
      {canPublish
        ? "Publish a guide so everyone on this customer account can find it."
        : "Your team has not published any customer Help articles yet."}
    </Typography>
    {canPublish && onPublish && (
      <Button variant="contained" onClick={onPublish} sx={{ mt: 2.5 }}>
        Publish the first article
      </Button>
    )}
  </Box>
);
